import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, isToday, isYesterday, differenceInDays } from "date-fns";
import { Bell, Check, Trash2, ArrowLeft, MessageSquare, Shield, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../api/axios";
import { useChatContext } from "../context/ChatContext";
import { useAuth } from "../context/AuthContext";

export default function Notifications() {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isMarkingAll, setIsMarkingAll] = useState(false);
    const { socket } = useChatContext();
    const { user } = useAuth();

    const fetchNotifications = async () => {
        try {
            setIsLoading(true);
            const res = await api.get("/notifications");
            if (res.data) {
                setNotifications(res.data);
            }
        } catch (err) {
            console.error("Failed to load notifications:", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    // 📡 Live Notification Stream Listener
    useEffect(() => {
        if (!socket) return;

        const handleNewMessage = (msg) => {
            // Ignore if I am the sender
            if (user?.id === msg.senderId) return;
            
            // Re-fetch the notifications cleanly so they get processed by the grouping logic
            fetchNotifications();
        };

        socket.on("receive_message", handleNewMessage);
        return () => socket.off("receive_message", handleNewMessage);
    }, [socket, user]);

    const handleMarkAllRead = async () => {
        try {
            setIsMarkingAll(true);
            await api.put("/notifications/read-all");
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            toast.success("All caught up!");
        } catch (err) {
            console.error("Failed to mark all as read:", err);
            toast.error("Failed to update notifications");
        } finally {
            setIsMarkingAll(false);
        }
    };

    const handleMarkRead = async (id, link) => {
        try {
            // Optimistic Update
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));

            // Hit actual endpoint
            await api.put(`/notifications/${id}/read`);

            // Redirect
            if (link && link !== "#") {
                navigate(link);
            }
        } catch (err) {
            console.error("Failed to mark notification read:", err);
        }
    };

    const handleClearAll = async () => {
        const confirmClear = window.confirm("Are you sure you want to delete all notifications?");
        if (!confirmClear) return;
        try {
            await api.delete("/notifications/delete-all");
            setNotifications([]);
            toast.success("Inbox cleared");
        } catch (err) {
            console.error("Failed to clear notifications:", err);
            toast.error("Failed to clear inbox");
        }
    };

    // Group notifications securely by type and link (for chat spam reduction)
    const groupNotifications = (list) => {
        const grouped = [];
        const chatGroups = new Map();

        list.forEach(note => {
            // For chats, we want to bundle them up to avoid spam
            if (note.type === "CHAT" && note.link) {
                const timeWindow = new Date(note.createdAt).getTime();

                // We find the sender name from 'New message from X'
                const match = note.content.match(/from (.*)$/);
                const sender = match ? match[1] : "someone";

                const key = note.link + "_" + sender;
                const existing = chatGroups.get(key);

                if (existing) {
                    existing.count += 1;
                    existing.idsToMark.push(note.id);
                    // Keep the newest timestamp
                    if (timeWindow > new Date(existing.createdAt).getTime()) {
                        existing.createdAt = note.createdAt;
                    }
                } else {
                    const newGroup = {
                        ...note,
                        isGrouped: true,
                        count: 1,
                        sender: sender,
                        idsToMark: [note.id]
                    };
                    chatGroups.set(key, newGroup);
                    grouped.push(newGroup);
                }
            } else {
                // Safe standard individual items
                grouped.push(note);
            }
        });

        // Fix the grouped content payload strings securely without exposing actual chat message
        grouped.forEach(item => {
            if (item.isGrouped && item.count > 1) {
                item.content = `You have ${item.count} new messages from ${item.sender}`;
            }
        });

        // Re-sort by latest
        return grouped.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    };


    // Segregate by Date Window
    const segregateByDate = (list) => {
        const today = [];
        const yesterday = [];
        const last7Days = [];
        const older = [];

        list.forEach(item => {
            const d = new Date(item.createdAt);
            if (isToday(d)) {
                today.push(item);
            } else if (isYesterday(d)) {
                yesterday.push(item);
            } else if (differenceInDays(new Date(), d) <= 7) {
                last7Days.push(item);
            } else {
                older.push(item);
            }
        });

        return { today, yesterday, last7Days, older };
    };

    const processedList = groupNotifications(notifications);
    const { today, yesterday, last7Days, older } = segregateByDate(processedList);

    const getStyleParams = (type) => {
        switch (type) {
            case "CHAT":
                return {
                    icon: <MessageSquare className="w-5 h-5 text-blue-500" />,
                    bg: "bg-blue-50 dark:bg-blue-900/20"
                };
            case "SYSTEM":
            case "CERTIFICATION":
                return {
                    icon: <Shield className="w-5 h-5 text-purple-500" />,
                    bg: "bg-purple-50 dark:bg-purple-900/20"
                };
            case "WARNING":
            case "ALERT":
                return {
                    icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
                    bg: "bg-amber-50 dark:bg-amber-900/20"
                };
            default:
                return {
                    icon: <Bell className="w-5 h-5 text-gray-500" />,
                    bg: "bg-gray-50 dark:bg-gray-800"
                };
        }
    };


    const NotificationRow = ({ note }) => {
        const style = getStyleParams(note.type);

        return (
            <div
                onClick={() => {
                    if (note.isGrouped) {
                        // Mark all inside group LOCALLY first
                        const groupIds = note.idsToMark;
                        setNotifications(prev => prev.map(n => groupIds.includes(n.id) ? { ...n, isRead: true } : n));

                        // Hit API (Silent)
                        groupIds.forEach(gid => api.put(`/notifications/${gid}/read`).catch(() => null));
                        navigate(note.link);
                    } else {
                        handleMarkRead(note.id, note.link);
                    }
                }}
                className={`relative p-4 sm:p-5 flex gap-4 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-800
          ${!note.isRead ? style.bg : "bg-white dark:bg-gray-900 opacity-70"} 
          hover:opacity-100`}
            >
                <div className="shrink-0 mt-1">
                    <div className={`p-2 rounded-full ${!note.isRead ? 'bg-white dark:bg-gray-800 shadow-sm' : ''}`}>
                        {style.icon}
                    </div>
                </div>

                <div className="flex-1 min-w-0">
                    <p className={`text-md ${!note.isRead ? "font-semibold text-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-400"}`}>
                        {note.content}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400 font-medium">
                            {format(new Date(note.createdAt), 'h:mm a')}
                        </span>
                    </div>
                </div>

                {!note.isRead && (
                    <div className="shrink-0 flex items-center">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500 mt-2"></div>
                    </div>
                )}
            </div>
        );
    };

    const Section = ({ title, items }) => {
        if (!items || items.length === 0) return null;
        return (
            <div className="mb-6">
                <h3 className="px-4 py-2 text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50/50 dark:bg-gray-800/50 sticky top-0 backdrop-blur-sm z-10 border-y border-gray-100 dark:border-gray-800">
                    {title}
                </h3>
                <div className="flex flex-col">
                    {items.map(note => <NotificationRow key={note.id} note={note} />)}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 pb-20">
            {/* Header */}
            <div className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 -ml-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                            Notifications
                        </h1>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleMarkAllRead}
                            disabled={isMarkingAll || notifications.length === 0}
                            className="px-3 py-1.5 text-sm font-medium text-brand-blue hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition flex items-center gap-1.5 disabled:opacity-50"
                        >
                            {isMarkingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            <span className="hidden sm:inline">Mark all read</span>
                        </button>
                        <button
                            onClick={handleClearAll}
                            disabled={notifications.length === 0}
                            className="px-3 py-1.5 text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition flex items-center gap-1.5 ml-2 disabled:opacity-50"
                        >
                            <Trash2 className="w-4 h-4" />
                            <span className="hidden sm:inline">Clear All</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="max-w-3xl mx-auto flex-1">
                {isLoading ? (
                    <div className="p-8 text-center text-gray-500">
                        <div className="animate-spin w-8 h-8 mx-auto border-2 border-brand-blue border-t-transparent rounded-full" />
                        <p className="mt-4">Loading your journey...</p>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="p-12 text-center flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                            <Bell className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">All caught up!</h3>
                        <p className="text-gray-500 dark:text-gray-400 mt-2">No new notifications in your sanctuary at the moment.</p>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-gray-900 sm:rounded-2xl sm:shadow-sm sm:border sm:border-gray-200 dark:sm:border-gray-800 sm:my-6 overflow-hidden">
                        <Section title="Today" items={today} />
                        <Section title="Yesterday" items={yesterday} />
                        <Section title="Last 7 Days" items={last7Days} />
                        <Section title="Older" items={older} />
                    </div>
                )}
            </main>
        </div >
    );
}
